import React, { useState, useRef, useEffect } from 'react';
import './Chat.css';

function MetricsPanel({ metrics }) {
  const [expanded, setExpanded] = useState(false);
  const { latency, tokens, cost } = metrics;

  const formatMs = (ms) => ms != null ? `${ms.toLocaleString()} ms` : '—';
  const formatCost = (val) => val != null ? `$${val.toFixed(6)}` : '—';

  return (
    <div className="metrics-panel">
      <button
        className="metrics-toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="metrics-toggle-icon">{expanded ? '▾' : '▸'}</span>
        <span className="metrics-summary">
          {tokens.totalTokens} tokens · {formatMs(latency.totalResponseTimeMs)}
          {cost.totalCost != null && ` · ${formatCost(cost.totalCost)}`}
        </span>
      </button>
      {expanded && (
        <div className="metrics-details">
          <div className="metrics-section">
            <div className="metrics-section-title">Latency</div>
            <div className="metrics-row">
              <span>Bedrock latency</span><span>{formatMs(latency.bedrockLatencyMs)}</span>
            </div>
            <div className="metrics-row">
              <span>Total response time</span><span>{formatMs(latency.totalResponseTimeMs)}</span>
            </div>
          </div>
          <div className="metrics-section">
            <div className="metrics-section-title">Tokens</div>
            <div className="metrics-row">
              <span>Input</span><span>{tokens.inputTokens.toLocaleString()}</span>
            </div>
            <div className="metrics-row">
              <span>Output</span><span>{tokens.outputTokens.toLocaleString()}</span>
            </div>
            <div className="metrics-row">
              <span>Total</span><span>{tokens.totalTokens.toLocaleString()}</span>
            </div>
          </div>
          <div className="metrics-section">
            <div className="metrics-section-title">Cost Estimate</div>
            <div className="metrics-row">
              <span>Input</span><span>{formatCost(cost.inputCost)}</span>
            </div>
            <div className="metrics-row">
              <span>Output</span><span>{formatCost(cost.outputCost)}</span>
            </div>
            <div className="metrics-row metrics-row-total">
              <span>Total</span><span>{formatCost(cost.totalCost)}</span>
            </div>
            {cost.note && <div className="metrics-note">{cost.note}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function Chat({ onLogout, idToken, config, models: propModels, endpoints: propEndpoints, onEvaluate }) {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState(propModels || []);
  const [endpoints, setEndpoints] = useState(propEndpoints || []);
  const [selectedOption, setSelectedOption] = useState('');
  const [documentContext, setDocumentContext] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync models from prop
  useEffect(() => {
    if (propModels && propModels.length > 0) {
      setModels(propModels);
    }
  }, [propModels]);

  // Sync endpoints from prop
  useEffect(() => {
    if (propEndpoints && propEndpoints.length > 0) {
      setEndpoints(propEndpoints);
    }
  }, [propEndpoints]);

  // Build unified options list: custom endpoints first, then bedrock models
  const unifiedOptions = [
    ...endpoints.map((ep) => ({ id: `custom:${ep.id}`, name: `Custom: ${ep.name}`, source: 'custom', modelId: ep.id })),
    ...models.map((m) => ({ id: `bedrock:${m.id}`, name: m.name, source: 'bedrock', modelId: m.id })),
  ];

  // Set default selection when options become available
  useEffect(() => {
    if (!selectedOption && unifiedOptions.length > 0) {
      setSelectedOption(unifiedOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unifiedOptions.length, selectedOption]);

  // Derive modelSource and modelId from the selected unified option
  const currentOption = unifiedOptions.find((o) => o.id === selectedOption);
  const modelSource = currentOption?.source || 'bedrock';
  const activeModelId = currentOption?.modelId || '';

  // PDF upload handler — extracts text client-side using pdf.js
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (allowlist: PDF only)
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are allowed.');
      e.target.value = '';
      return;
    }

    // Validate file size (max 20MB)
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('File size must be under 20MB.');
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      // Validate page count (max 20 pages)
      if (pdf.numPages > 20) {
        alert(`PDF has ${pdf.numPages} pages. Maximum allowed is 20 pages.`);
        e.target.value = '';
        setIsUploading(false);
        return;
      }

      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(' ');
        fullText += `\n--- Page ${i} ---\n${pageText}`;
      }

      setDocumentContext(fullText.trim());
      setDocumentName(file.name);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `📄 Document loaded: **${file.name}** (${pdf.numPages} pages). You can now ask questions about this document. The document will remain in context for the duration of this session.`,
        },
      ]);
    } catch (error) {
      console.error('PDF parsing error:', error);
      alert(`Failed to parse PDF: ${error.message}`);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleClearDocument = () => {
    setDocumentContext('');
    setDocumentName('');
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '📄 Document removed from context.' },
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prompt.trim()) return;

    const userMessage = { role: 'user', content: prompt };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt('');
    setIsLoading(true);

    try {
      // Get API URL from config
      const apiUrl = config.apiUrl;

      const response = await fetch(`${apiUrl}chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          modelId: activeModelId,
          modelSource,
          documentContext: documentContext || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        metrics: data.metrics || null,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling API:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.message}. Please check the API URL configuration.`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-card">
        <div className="chat-header">
          <h1>Bedrock Chat</h1>
          <div className="header-controls">
            <select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="model-select"
            >
              {unifiedOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <input
              type="file"
              accept=".pdf,application/pdf"
              ref={fileInputRef}
              onChange={handlePdfUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="upload-button"
              disabled={isUploading}
              title="Upload PDF (max 20 pages)"
            >
              {isUploading ? '⏳' : '📄'} {isUploading ? 'Loading...' : 'Upload PDF'}
            </button>
            {documentName && (
              <span className="document-badge" title={documentName}>
                📎 {documentName.length > 15 ? documentName.slice(0, 15) + '...' : documentName}
                <button onClick={handleClearDocument} className="clear-doc-button" title="Remove document">×</button>
              </span>
            )}
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
            <button onClick={onEvaluate} className="eval-mode-button">
              Evaluate
            </button>
          </div>
        </div>

        <div className="messages-container">
          {messages.length === 0 && (
            <div className="empty-state">
              <h2>Start a conversation</h2>
              <p>Ask me anything using {currentOption?.name || 'AI'}</p>
              <p className="empty-state-hint">💡 Upload a PDF to ask questions about a document</p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
            >
              <div className="message-label">
                {message.role === 'user' ? 'You' : 'Claude'}
              </div>
              <div className="message-content">{message.content}</div>
              {message.metrics && (
                <MetricsPanel metrics={message.metrics} />
              )}
            </div>
          ))}

          {isLoading && (
            <div className="message assistant-message">
              <div className="message-label">Claude</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="input-form">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            className="prompt-input"
          />
          <button
            type="submit"
            disabled={isLoading || !prompt.trim()}
            className="send-button"
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Chat;
